import React, { useEffect, useState } from "react";
import { Table, Input, Tag, Space, Popconfirm, Select } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteUser,
  fetchAllUsers,
  setUserDetailsIsEdit,
} from "./redux/actions";
import SpinnerComponent from "../../components/spinner";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import CustomPagination from "../../components/customPagination/CustomPagination";
import { capitalizeFirstLetterofEachWord } from "../../globalUtils/capitalizeFirstLetterofEachWord";
import { statusOptions, userTypeOptions } from "./utils";
import { fetchDepartments } from "../ActivityTypes/redux/actions";
import AMultiSelect from "../../components/AMultiSelect";

const { Option } = Select;

const UsersRegisterTable = ({ canUpdate }) => {
  const [sortedInfo, setSortedInfo] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [currentDelRecord, setCurrentDelRecord] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(() => 1);
  const [resultPerPage, setResultPerPage] = useState(() => 10);
  const [req, setReq] = useState(false);
  const [search, setSearch] = useState({});
  const [userTypeCheckedList, setUserTypeCheckedList] = useState([]);
  const [statusCheckedList, setStatusCheckedList] = useState([]);
  const [departmentCheckedList, setDepartmentCheckedList] = useState([]);
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);

  // Handler for user type AMultiSelect onSelectChange: only updates userTypeCheckedList
  const handleUserTypeSelectionChange = (value) => {
    if (value.includes("all")) {
      setUserTypeCheckedList(userTypeOptions.map((i) => i.value));
    } else if (value.includes("not-all")) {
      setUserTypeCheckedList([]);
    } else {
      setUserTypeCheckedList(value);
    }
  };

  // Handler for user type AMultiSelect "Done" button: updates main search state
  const handleUserTypeFilterApply = () => {
    setSearch(prevSearch => ({ ...prevSearch, userType: userTypeCheckedList.length > 0 ? userTypeCheckedList.join(',') : undefined }));
    debouncedDefaultPageOneCallApi();
  };

  // Handler for status AMultiSelect onSelectChange: only updates statusCheckedList
  const handleStatusSelectionChange = (value) => {
    if (value.includes("all")) {
      setStatusCheckedList(statusOptions.map((i) => i.value));
    } else if (value.includes("not-all")) {
      setStatusCheckedList([]);
    } else {
      setStatusCheckedList(value);
    }
  };

  // Handler for status AMultiSelect "Done" button: updates main search state
  const handleStatusFilterApply = () => {
    setSearch(prevSearch => ({ ...prevSearch, status: statusCheckedList.length > 0 ? statusCheckedList.join(',') : undefined }));
    debouncedDefaultPageOneCallApi();
  };

  // Handler for department AMultiSelect onSelectChange: only updates departmentCheckedList
  const handleDepartmentSelectionChange = (value) => {
    if (value.includes("all")) {
      setDepartmentCheckedList(departmentOptions.map((i) => i.value));
    } else if (value.includes("not-all")) {
      setDepartmentCheckedList([]);
    } else {
      setDepartmentCheckedList(value);
    }
  };

  // Handler for department AMultiSelect "Done" button: updates main search state
  const handleDepartmentFilterApply = () => {
    setSearch(prevSearch => ({ ...prevSearch, departmentId: departmentCheckedList.length > 0 ? departmentCheckedList.join(',') : undefined }));
    debouncedDefaultPageOneCallApi();
  };

  const { allUsers, fetchUsersLoading, deleteUserLoading, totalRecords } =
    useSelector((state) => state.userManagerReducer);
  const { allDepartments } = useSelector((state) => state.departmentReducer || { allDepartments: [] });
  const { user } = useSelector((state) => state.authReducer);

  const canDeleteUsers = user?.permissions?.user_manager?.delete || false;

  // Format departments for AMultiSelect
  const departmentOptions = allDepartments && Array.isArray(allDepartments) ? allDepartments.map((dept) => ({
    label: dept.name || dept.department,
    value: dept.id || dept._id,
  })) : [];

  // Fetch departments on component mount
  useEffect(() => {
    dispatch(fetchDepartments({ search: {} }));
  }, [dispatch]);

  useEffect(() => {
    if (page && resultPerPage && page > 0 && resultPerPage > 0) {
      dispatch(
        fetchAllUsers({
          page: page,
          perPage: resultPerPage,
          search,
          sort: sortedInfo?.order && sortedInfo?.columnKey,
          order: sortedInfo?.order
            ? sortedInfo?.order === "ascend"
              ? "asc"
              : "desc"
            : null,
        })
      );
    }
  }, [req]);


  useEffect(() => {
    let paramsPage = 1;
    if (paramsPage && resultPerPage && paramsPage > 0 && resultPerPage > 0) {
      dispatch(
        fetchAllUsers({ page: paramsPage, perPage: resultPerPage, search })
      );
      setPage(1);
    }
  }, [defaultPageOneReq]);

  useEffect(() => {
    debouncedCallApi();
  }, [sortedInfo]);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 1000)
  );

  useEffect(() => {
    setDataSource(allUsers);
  }, [allUsers]);

  useEffect(() => {
    setFetchLoading(fetchUsersLoading);
  }, [fetchUsersLoading]);

  // useEffect(() => {
  //   setFetchLoading(deleteUserLoading);
  // }, [deleteUserLoading]);

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  const handleDelete = (record) => {
    setCurrentDelRecord(record);
    dispatch(deleteUser(record));
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        setSearch({
          ...search,
          [key]: e.target.value,
        });
        debouncedDefaultPageOneCallApi();
      }}
    />
  );

  const handleNavigate = (id) =>
    navigate(`/settings/users-manager/update-user/${id}`);

  const columns = [
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">First name</div>
            <div>{searchInput("firstName")}</div>
          </div>
        );
      },
      dataIndex: "firstName",
      key: "firstName",
      // sorter: (a, b) => a.first_name.localeCompare(b.first_name),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "firstName" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (firstName, { id }) => (
        <div onClick={() => handleNavigate(id)}>{firstName}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">last name</div>
            <div>{searchInput("lastName")}</div>
          </div>
        );
      },
      dataIndex: "lastName",
      key: "lastName",
      // sorter: (a, b) => a.surname.localeCompare(b.surname),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "lastName" && sortedInfo?.order,
      ellipsis: true,
      width: "20%",
      render: (lastName, { id }) => (
        <div onClick={() => handleNavigate(id)}>{lastName}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">department</div>
            <div>
              <AMultiSelect
                dropdownClassName="department-selector"
                debouncedCallApi={handleDepartmentFilterApply}
                disabled={false}
                placeholder=""
                selectTags={departmentCheckedList}
                onSelectChange={handleDepartmentSelectionChange}
                data={departmentOptions}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "departmentName",
      key: "departmentId",
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === "departmentId" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (name, { id, departmentName }) => (
        <div onClick={() => handleNavigate(id)}>{departmentName}</div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">job title</div>
            <div>{searchInput("jobTitle")}</div>
          </div>
        );
      },
      dataIndex: "jobTitle",
      key: "jobTitle",
      // sorter: (a, b) => a.job_title.localeCompare(b.job_title),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "jobTitle" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (jobTitle, { id }) => (
        <div onClick={() => handleNavigate(id)}>{jobTitle}</div>
      ),
    },

    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">user type</div>
            <div>
              <AMultiSelect
                dropdownClassName="user-type-selector"
                debouncedCallApi={handleUserTypeFilterApply}
                disabled={false}
                placeholder=""
                selectTags={userTypeCheckedList}
                onSelectChange={handleUserTypeSelectionChange}
                data={userTypeOptions}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "userType",
      key: "userType",
      // sorter: (a, b) => a.user_type.localeCompare(b.user_type),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "userType" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (userType, { id }) => (
        <div onClick={() => handleNavigate(id)}>
          {userType && capitalizeFirstLetterofEachWord(userType)}
        </div>
      ),
    },

    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">status</div>
            {/* <div>{searchInput("status")} </div> */}
            <div>
              <AMultiSelect
                dropdownClassName="status-selector"
                debouncedCallApi={handleStatusFilterApply}
                disabled={false}
                placeholder=""
                selectTags={statusCheckedList}
                onSelectChange={handleStatusSelectionChange}
                data={statusOptions}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "status",
      key: "status",
      // sorter: (a, b) => a.status.localeCompare(b.status),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "status" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      align: "center",
      render: (status, { id }) => {
        let color;
        let text;
        switch (status) {
          case "active":
            color = "#a7e0a5";
            text = "Active";
            break;
          case "inactive":
            color = "#fcb9b8";
            text = "Inactive";
            break;
          default:
            break;
        }
        return (
          <Tag
            onClick={() => handleNavigate(id)}
            color={color}
            style={{
              color: "#383838",
              fontFamily: "poppins",
              width: "70px",
              height: "20px",
              borderRadius: "10px",
              textAlign: "center",
            }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: null,
      dataIndex: "",
      key: "action",
      //   width: "15%",
      align: "center",
      render: (_, record) => {
        if (!canUpdate) {
          return;
        }

        return deleteUserLoading && record.id === currentDelRecord.id ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={15} align="baseline">
            <EditIcon
              width={18}
              height={18}
              className="custom-icon"
              onClick={() => {
                handleNavigate(record.id);
                dispatch(setUserDetailsIsEdit(true));
              }}
            />
            {canDeleteUsers && (
              <Popconfirm
                title="Are you sure you want to delete this User?"
                placement="topRight"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteIcon width={18} height={18} className="custom-icon" />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className="user-register-table-container">
        <Table
          className="special-table users-manager-table"
          columns={columns}
          scroll={{ x: true }}
          onChange={handleChange}
          dataSource={dataSource}
          pagination={false}
          sortUpIcon={<UpOutlined />}
          sortDownIcon={<DownOutlined />}
          loading={fetchLoading}
        />
      </div>
      <style>{`
        /* Specific selectors for User Manager table only */
        .user-register-table-container .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(0, 154, 208, 0.3) !important;
        }
        
        /* Remove the bottom border from the table to avoid double borders */
        .user-register-table-container .ant-table {
          border-bottom: none !important;
        }
        
        /* Remove any default styling that might be causing double borders */
        .user-register-table-container .ant-table-container::after {
          display: none !important;
        }
      `}</style>
      <CustomPagination
        page={page}
        setPage={setPage}
        resultPerPage={resultPerPage}
        setResultPerPage={setResultPerPage}
        debouncedCallApi={debouncedCallApi}
        totalRecord={totalRecords}
      />
    </>
  );
};

export default UsersRegisterTable;